import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import toast from 'react-hot-toast';
import { paymentSettingsService, PaymentSettings, DEFAULT_CASHFREE_CONFIG } from '../services/paymentSettingsService';
import { adminService } from '../services/adminService';

// Razorpay/Paytm/PhonePe are not integrated yet — hide their settings cards
// until they are. Flip to true to show them again.
const SHOW_UNINTEGRATED_GATEWAYS = false;

const PaymentGatewaySettings: React.FC = () => {
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [webhookTesting, setWebhookTesting] = useState(false);

  const runWebhookTest = async () => {
    setWebhookTesting(true);
    try {
      const result = await adminService.testCashfreeWebhook();
      if (result.signatureAccepted) {
        toast.success(`Webhook reachable and signature accepted (${result.latencyMs}ms)`);
      } else if (result.reachable) {
        toast.error(`Webhook reachable but rejected (HTTP ${result.httpStatus}) — check the secret key`);
      } else {
        toast.error(result.hint || result.error || 'Webhook URL is not reachable');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Webhook test failed');
    } finally {
      setWebhookTesting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setPaymentLoading(true);
        const data = await paymentSettingsService.getSettings();
        // Settings saved before the field existed have no cashfree block
        setPaymentSettings({ ...data, cashfree: data.cashfree || DEFAULT_CASHFREE_CONFIG });
      } catch {
        toast.error('Failed to load payment settings');
      } finally {
        setPaymentLoading(false);
      }
    };
    load();
  }, []);

  const savePaymentSettings = async () => {
    if (!paymentSettings) return;
    try {
      setPaymentSaving(true);
      const data = await paymentSettingsService.updateSettings(paymentSettings);
      setPaymentSettings(data);
      toast.success('Payment settings updated');
    } catch {
      toast.error('Failed to save payment settings');
    } finally {
      setPaymentSaving(false);
    }
  };

  if (paymentLoading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  if (!paymentSettings) {
    return <Alert severity="error">Failed to load payment settings</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Payment Gateways</Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>General</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Currency" value="INR" disabled />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Wallet credit value (₹ per credit)"
                  value={paymentSettings.creditValueInr ?? 10}
                  onChange={(e) => setPaymentSettings(prev => prev ? {
                    ...prev,
                    creditValueInr: Number(e.target.value),
                  } : prev)}
                  helperText="Used for upgrade proration and wallet-mode refunds"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {SHOW_UNINTEGRATED_GATEWAYS && (
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Razorpay</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentSettings.razorpay.enabled || false}
                  onChange={(e) => setPaymentSettings(prev => prev ? {
                    ...prev,
                    razorpay: { ...prev.razorpay, enabled: e.target.checked },
                  } : prev)}
                />
              }
              label="Enable Razorpay"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Mode</InputLabel>
              <Select
                value={paymentSettings.razorpay.mode || 'test'}
                onChange={(e) => setPaymentSettings(prev => prev ? {
                  ...prev,
                  razorpay: { ...prev.razorpay, mode: e.target.value as 'test' | 'live' },
                } : prev)}
                label="Mode"
              >
                <MenuItem value="test">Test</MenuItem>
                <MenuItem value="live">Live</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Key ID"
              value={paymentSettings.razorpay.keyId || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                razorpay: { ...prev.razorpay, keyId: e.target.value },
              } : prev)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Key Secret"
              value={paymentSettings.razorpay.keySecret || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                razorpay: { ...prev.razorpay, keySecret: e.target.value },
              } : prev)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Webhook Secret"
              value={paymentSettings.razorpay.webhookSecret || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                razorpay: { ...prev.razorpay, webhookSecret: e.target.value },
              } : prev)}
            />
          </Paper>
        </Grid>
        )}

        {SHOW_UNINTEGRATED_GATEWAYS && (
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Paytm</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentSettings.paytm.enabled || false}
                  onChange={(e) => setPaymentSettings(prev => prev ? {
                    ...prev,
                    paytm: { ...prev.paytm, enabled: e.target.checked },
                  } : prev)}
                />
              }
              label="Enable Paytm"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Mode</InputLabel>
              <Select
                value={paymentSettings.paytm.mode || 'test'}
                onChange={(e) => setPaymentSettings(prev => prev ? {
                  ...prev,
                  paytm: { ...prev.paytm, mode: e.target.value as 'test' | 'live' },
                } : prev)}
                label="Mode"
              >
                <MenuItem value="test">Test</MenuItem>
                <MenuItem value="live">Live</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Merchant ID"
              value={paymentSettings.paytm.merchantId || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                paytm: { ...prev.paytm, merchantId: e.target.value },
              } : prev)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Merchant Key"
              value={paymentSettings.paytm.merchantKey || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                paytm: { ...prev.paytm, merchantKey: e.target.value },
              } : prev)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Website"
              value={paymentSettings.paytm.website || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                paytm: { ...prev.paytm, website: e.target.value },
              } : prev)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Industry Type ID"
              value={paymentSettings.paytm.industryTypeId || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                paytm: { ...prev.paytm, industryTypeId: e.target.value },
              } : prev)}
            />
          </Paper>
        </Grid>
        )}

        {SHOW_UNINTEGRATED_GATEWAYS && (
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>PhonePe</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentSettings.phonepe.enabled || false}
                  onChange={(e) => setPaymentSettings(prev => prev ? {
                    ...prev,
                    phonepe: { ...prev.phonepe, enabled: e.target.checked },
                  } : prev)}
                />
              }
              label="Enable PhonePe"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Mode</InputLabel>
              <Select
                value={paymentSettings.phonepe.mode || 'test'}
                onChange={(e) => setPaymentSettings(prev => prev ? {
                  ...prev,
                  phonepe: { ...prev.phonepe, mode: e.target.value as 'test' | 'live' },
                } : prev)}
                label="Mode"
              >
                <MenuItem value="test">Test</MenuItem>
                <MenuItem value="live">Live</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Merchant ID"
              value={paymentSettings.phonepe.merchantId || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                phonepe: { ...prev.phonepe, merchantId: e.target.value },
              } : prev)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Salt Key"
              value={paymentSettings.phonepe.saltKey || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                phonepe: { ...prev.phonepe, saltKey: e.target.value },
              } : prev)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Salt Index"
              value={paymentSettings.phonepe.saltIndex || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                phonepe: { ...prev.phonepe, saltIndex: e.target.value },
              } : prev)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Redirect URL"
              value={paymentSettings.phonepe.redirectUrl || ''}
              onChange={(e) => setPaymentSettings(prev => prev ? {
                ...prev,
                phonepe: { ...prev.phonepe, redirectUrl: e.target.value },
              } : prev)}
            />
          </Paper>
        </Grid>
        )}

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography variant="h6">Cashfree</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentSettings.cashfree?.enabled || false}
                    onChange={(e) => setPaymentSettings(prev => prev ? {
                      ...prev,
                      cashfree: { ...(prev.cashfree || DEFAULT_CASHFREE_CONFIG), enabled: e.target.checked },
                    } : prev)}
                  />
                }
                label="Enabled"
              />
            </Box>

            <FormControl fullWidth sx={{ my: 2 }}>
              <InputLabel>Active mode</InputLabel>
              <Select
                value={paymentSettings.cashfree?.mode || 'test'}
                onChange={(e) => setPaymentSettings(prev => prev ? {
                  ...prev,
                  cashfree: { ...(prev.cashfree || DEFAULT_CASHFREE_CONFIG), mode: e.target.value as 'test' | 'live' },
                } : prev)}
                label="Active mode"
              >
                <MenuItem value="test">Test (sandbox) — no real money moves</MenuItem>
                <MenuItem value="live">Live — real payments</MenuItem>
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              {/* Test credentials */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor: paymentSettings.cashfree?.mode !== 'live' ? 'success.main' : undefined,
                    borderWidth: paymentSettings.cashfree?.mode !== 'live' ? 2 : 1,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <Typography variant="subtitle2" fontWeight={700}>Test credentials</Typography>
                    {paymentSettings.cashfree?.mode !== 'live' && <Chip size="small" color="success" label="active" />}
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Test App ID"
                    value={paymentSettings.cashfree?.testAppId || ''}
                    onChange={(e) => setPaymentSettings(prev => prev ? {
                      ...prev,
                      cashfree: { ...(prev.cashfree || DEFAULT_CASHFREE_CONFIG), testAppId: e.target.value },
                    } : prev)}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Test Secret Key"
                    value={paymentSettings.cashfree?.testSecretKey || ''}
                    onChange={(e) => setPaymentSettings(prev => prev ? {
                      ...prev,
                      cashfree: { ...(prev.cashfree || DEFAULT_CASHFREE_CONFIG), testSecretKey: e.target.value },
                    } : prev)}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Test Webhook Secret"
                    value={paymentSettings.cashfree?.testWebhookSecret || ''}
                    onChange={(e) => setPaymentSettings(prev => prev ? {
                      ...prev,
                      cashfree: { ...(prev.cashfree || DEFAULT_CASHFREE_CONFIG), testWebhookSecret: e.target.value },
                    } : prev)}
                    helperText="From Dashboard → Webhooks (test). Blank = sign with API secret."
                  />
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Sandbox test card: 4111 1111 1111 1111 · any future expiry/CVV · OTP 111000
                  </Typography>
                </Paper>
              </Grid>

              {/* Live credentials */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor: paymentSettings.cashfree?.mode === 'live' ? 'error.main' : undefined,
                    borderWidth: paymentSettings.cashfree?.mode === 'live' ? 2 : 1,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <Typography variant="subtitle2" fontWeight={700}>Live credentials</Typography>
                    {paymentSettings.cashfree?.mode === 'live' && <Chip size="small" color="error" label="active" />}
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Live App ID"
                    value={paymentSettings.cashfree?.liveAppId || ''}
                    onChange={(e) => setPaymentSettings(prev => prev ? {
                      ...prev,
                      cashfree: { ...(prev.cashfree || DEFAULT_CASHFREE_CONFIG), liveAppId: e.target.value },
                    } : prev)}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Live Secret Key"
                    value={paymentSettings.cashfree?.liveSecretKey || ''}
                    onChange={(e) => setPaymentSettings(prev => prev ? {
                      ...prev,
                      cashfree: { ...(prev.cashfree || DEFAULT_CASHFREE_CONFIG), liveSecretKey: e.target.value },
                    } : prev)}
                    sx={{ mb: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Live Webhook Secret"
                    value={paymentSettings.cashfree?.liveWebhookSecret || ''}
                    onChange={(e) => setPaymentSettings(prev => prev ? {
                      ...prev,
                      cashfree: { ...(prev.cashfree || DEFAULT_CASHFREE_CONFIG), liveWebhookSecret: e.target.value },
                    } : prev)}
                    helperText="From Dashboard → Webhooks (production). Blank = sign with API secret."
                  />
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    From Cashfree Dashboard → Developers → API Keys (production)
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Box mt={2}>
              <Button
                variant="outlined"
                size="small"
                disabled={webhookTesting}
                onClick={runWebhookTest}
              >
                {webhookTesting ? 'Testing…' : `Send test webhook (${paymentSettings.cashfree?.mode === 'live' ? 'live' : 'test'} secret)`}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                Sends a signed dummy event to the public webhook URL — proves reachability and the saved secret for the
                active mode. Save settings first. Webhook URL: api.ridesaathi.in/api/payments/webhook/cashfree
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={savePaymentSettings}
            disabled={paymentSaving || paymentLoading}
          >
            {paymentSaving ? 'Saving...' : 'Save Payment Settings'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentGatewaySettings;
